import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar } from 'src/@/components/ui/avatar';
import { Button } from 'src/@/components/ui/button';
import { Dialog, DialogContent, DialogTrigger } from 'src/@/components/ui/dialog';
import Scrollbar from 'src/components/Scrollbar';
import classNames from 'src/utils/classNames';
import Iconify from 'src/components/Iconify';
import { Input } from 'src/@/components/ui/input';
import { useSelector } from 'src/redux/store';
import { useDispatch } from 'react-redux';
import { getChatInternoBuscarChat, getChatInternoBuscarContatos, setSelectedChat } from 'src/redux/slices/staff';
import useAuth from 'src/hooks/useAuth';
import { RowInput } from 'src/components/forms/RowInput';
import { useNavigate } from 'react-router';
import v2_actionChatInternoCriarGrupo from 'src/actions/v2/chats/chat-interno/v2_actionChatInternoCriarGrupo';
import v2_actionChatInternoEditarGrupo from 'src/actions/v2/chats/chat-interno/v2_actionChatInternoEditarGrupo';
import v2_actionChatInternoCriar from 'src/actions/v2/chats/chat-interno/v2_actionChatInternoCriar';
import { ButtonLoading } from 'src/@/components/use/ButtonLoading';

const NewChat = ({ isEdit, icon, listaChats, onCallback, customTrigger }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cliente, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState([]);
  const [query, setQuery] = useState('');
  const [groupName, setGroupName] = useState('');
  const { listaContatos, staff } = useSelector((state) => state.staff);
  const [loading, setLoading] = useState(false);

  // Admin do grupo (primeiro participante)
  const admin = useMemo(() => {
    if (isEdit && staff?.isGroup && staff.people?.length > 0) {
      return staff.people[0];
    }
    return null;
  }, [isEdit, staff]);

  const contacts = useMemo(() => {
    if (!listaContatos) return [];
    return listaContatos
      .filter((contact) => {
        if (!query) return true;
        return (
          contact?.nomeFantasia.toLowerCase().includes(query) ||
          contact?.email.toLowerCase().includes(query)
        );
      })
      .filter((contact) => {
        // Remove o próprio usuário da listagem
        if (user && contact._id === user.id) {
          return false;
        }
        // Remove o admin da listagem (ele será mostrado separadamente)
        if (admin && contact._id === admin._id) {
          return false;
        }
        return true;
      });
  }, [listaContatos, query, user, admin]);

  useEffect(() => {
    if (isEdit && staff?.isGroup) {
      // Garante que o admin (primeiro participante) sempre esteja incluído
      setSelectedContact(staff.people);
      setGroupName(staff.title);
    }
  }, [staff, open]);

  const handleDialogClose = () => {
    setOpen(!open);
    setSelectedContact([]);
  };

  const handleStartNewChat = async () => {
    console.log(selectedContact);
    if (selectedContact.length === 0) return alert(t('chat.internal.selectContactToStart'));
    if (
      selectedContact.some((contact) =>
        listaChats.some((chat) => !chat.isGroup && chat.people.some((p) => p._id === contact._id))
      )
    ) {
      const existingChat = listaChats.find(
        (chat) => !chat.isGroup && chat.people.some((p) => p._id === selectedContact[0]._id)
      );
      console.log(existingChat);
      if (existingChat) {
        setOpen(false);
        setSelectedContact([]);
        dispatch(getChatInternoBuscarChat(existingChat._id));
        dispatch(setSelectedChat(existingChat));
        navigate(`/chats/interno/${existingChat._id}`);
        onCallback();
      }
      return;
    } else {
      try {
        setLoading(true);
        const response = await v2_actionChatInternoCriar(cliente?._id, {
          counterpart: selectedContact[0]._id,
          user_id: user.id,
        });
        if (response.status === 200) {
          setOpen(false);
          setSelectedContact([]);
          dispatch(getChatInternoBuscarChat(response.data.room._id));
          dispatch(setSelectedChat(response.data.room));
          navigate(`/chats/interno/${response.data.room._id}`);
          handleDialogClose();
          onCallback();
          setLoading(false);
        }
      } catch (error) {
        setLoading(false);
        console.log(error);
      }
    }
  };

  const handleSave = () => {
    if (selectedContact?.length === 1 && staff.isGroup && isEdit)
      return alert(t('chat.internal.groupMustHaveMoreThanOne'));
    alert(t('chat.internal.updateChat'));
  };

  const handleSaveGroup = async () => {
    try {
      // Garante que o admin (primeiro participante) sempre esteja incluído
      let peoples = selectedContact.map((contact) => contact._id);
      if (isEdit && staff?.isGroup && staff.people?.length > 0) {
        const adminId = staff.people[0]._id;
        if (!peoples.includes(adminId)) {
          peoples = [adminId, ...peoples];
        }
      }
      let scope = {
        people: peoples,
        title: groupName,
      };
      let data = { scope };
      setLoading(true);
      const response = await v2_actionChatInternoEditarGrupo(staff._id, data);
      if (response.status === 200) {
        setOpen(false);
        setSelectedContact([]);
        setGroupName('');
        dispatch(getChatInternoBuscarChat(response.data.data._id));
        dispatch(setSelectedChat(response.data.data));
        navigate(`/chats/interno/${response.data.data._id}`);
        setLoading(false);
        onCallback();
      }
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  };

  const handleCreateGroup = async () => {
    try {
      setLoading(true);
      const response = await v2_actionChatInternoCriarGrupo(cliente?._id, {
        user_id: user.id,
        people: selectedContact,
        title: groupName,
      });
      if (response.status === 200) {
        console.log(response.data);
        setOpen(false);
        setSelectedContact([]);
        setGroupName('');
        dispatch(getChatInternoBuscarChat(response.data.data._id));
        dispatch(setSelectedChat(response.data.data));
        navigate(`/chats/interno/${response.data.data._id}`);
        setLoading(false);
        onCallback();
        handleDialogClose();
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const handleInputChange = (value) => {
    setQuery(value);
  };

  const handleSetSelectedContact = (contact) => {
    // Se for edição de grupo, não permite remover o admin (primeiro participante)
    if (admin && contact._id === admin._id) {
      console.log('O admin do grupo não pode ser removido');
      return;
    }

    setSelectedContact((prevSelectedContacts) => {
      if (!Array.isArray(prevSelectedContacts)) {
        prevSelectedContacts = [];
      }
      const contactExists = prevSelectedContacts.some((c) => c._id === contact._id);

      if (contactExists) {
        // Verifica novamente antes de remover - não permite remover o admin
        if (admin && contact._id === admin._id) {
          return prevSelectedContacts; // Não remove o admin
        }
        const filtered = prevSelectedContacts.filter((c) => c._id !== contact._id);
        // Garante que o admin sempre esteja presente
        if (admin && !filtered.some((c) => c._id === admin._id)) {
          return [admin, ...filtered];
        }
        return filtered;
      } else {
        const updated = [...prevSelectedContacts, contact];
        // Garante que o admin sempre esteja presente e no início
        if (admin && !updated.some((c) => c._id === admin._id)) {
          return [admin, ...updated];
        }
        // Se o admin já existe, garante que ele esteja no início
        if (admin && updated.some((c) => c._id === admin._id)) {
          const withoutAdmin = updated.filter((c) => c._id !== admin._id);
          return [admin, ...withoutAdmin];
        }
        return updated;
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogTrigger asChild>
        {customTrigger ? (
          <div
            onClick={() => {
              setOpen(true);
              dispatch(getChatInternoBuscarContatos(cliente?._id));
            }}
          >
            {customTrigger}
          </div>
        ) : isEdit ? (
          <Button
            size="icon"
            variant="outline"
            onClick={() => {
              setOpen(true);
              dispatch(getChatInternoBuscarContatos(cliente?._id));
            }}
          >
            <Iconify icon="ic:baseline-edit" />
          </Button>
        ) : icon ? (
          <Button
            className="flex items-center gap-2 h-7 bg-slate-700 dark:bg-[#383838]"
            onClick={() => {
              setOpen(true);
              dispatch(getChatInternoBuscarContatos(cliente?._id));
            }}
          >
            <Iconify icon="fluent:chat-add-24-regular" />
            {t('chat.internal.newChat')}
          </Button>
        ) : (
          <Button
            className="w-full bg-violet-600"
            onClick={() => {
              setOpen(true);
              dispatch(getChatInternoBuscarContatos(cliente?._id));
            }}
          >
            {t('chat.internal.newChat')}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        {contacts && (
          <div className="mt-6">
            <div className="text-center mb-6">
              <p className="dark:text-[#ABABAD]">{t('chat.internal.searchAndSelectContacts')}</p>
            </div>
            <Input
              placeholder={t('chat.internal.searchPlaceholder')}
              type="text"
              size="sm"
              prefix={<Iconify icon="ic:baseline-search" className="text-lg" />}
              onChange={(e) => handleInputChange(e.target.value)}
            />
            <div className="mt-4">
              <div className="mb-6">
                {/* Seção separada para o Admin */}
                {admin && (
                  <div className="mb-4 pb-4 border-b dark:border-[#383838]">
                    <p className="text-xs font-semibold text-gray-500 dark:text-[#ABABAD] mb-2">
                      {t('chat.internal.groupAdmin')}
                    </p>
                    <div
                      className={classNames(
                        'p-3 rounded-lg flex items-center justify-between bg-violet-100 dark:bg-violet-500/10 cursor-not-allowed opacity-75'
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <div>
                          <p className="text-sm font-bold dark:text-[#D1D2D3]">
                            {admin.nomeFantasia}
                            <span className="ml-2 text-xs text-gray-500 dark:text-[#ABABAD]">({t('chat.internal.adminTag')})</span>
                          </p>
                          <p className="text-xs dark:text-[#ABABAD]">{admin.email}</p>
                        </div>
                      </div>
                      <Iconify icon="material-symbols:check" className="text-2xl text-primary" />
                    </div>
                  </div>
                )}
                <Scrollbar className={classNames('overflow-y-auto h-80')}>
                  <div className="h-full pr-3 flex flex-col gap-2">
                    {contacts.map((contact) => {
                      const isSelected = selectedContact?.some((item) => item._id === contact._id);
                      return (
                        <div
                          key={contact._id}
                          className={classNames(
                            'p-3 rounded-lg flex items-center justify-between cursor-pointer hover:bg-gray-100 dark:hover:bg-[#2A2A2A]',
                            isSelected && 'bg-violet-100 dark:bg-violet-500/10',
                            contact._id === user._id && 'cursor-not-allowed opacity-50'
                          )}
                          role="button"
                          onClick={() => {
                            if (contact._id === user.id && isEdit) {
                              console.log('Você não pode adicionar ou remover a si mesmo');
                              return;
                            }
                            handleSetSelectedContact(contact);
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <div>
                              <p className="text-sm font-bold dark:text-[#D1D2D3]">{contact.nomeFantasia}</p>
                              <p className="text-xs dark:text-[#ABABAD]">{contact.email}</p>
                            </div>
                          </div>
                          {isSelected && (
                            <Iconify
                              icon="material-symbols:check"
                              className="text-2xl text-primary"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Scrollbar>
                {selectedContact?.length > 1 && (
                  <div className="mt-6">
                    <RowInput
                      label={t('chat.internal.groupName')}
                      input={
                        <Input
                          placeholder={t('chat.internal.groupNamePlaceholder')}
                          type="text"
                          value={groupName}
                          size="sm"
                          prefix={<Iconify icon="ic:baseline-search" className="text-lg" />}
                          onChange={(e) => setGroupName(e.target.value)}
                        />
                      }
                    />
                  </div>
                )}
              </div>
              {isEdit ? (
                <ButtonLoading
                  loading={loading}
                  disabled={
                    selectedContact?.length === 0 ||
                    (selectedContact?.length > 1 && !groupName)
                  }
                  className="w-full"
                  onClick={selectedContact?.length > 1 ? handleSaveGroup : handleSave}
                >
                  {selectedContact?.length > 1 ? t('chat.internal.updateGroup') : t('chat.internal.updateChat')}
                </ButtonLoading>
              ) : (
                <ButtonLoading
                  loading={loading}
                  disabled={
                    selectedContact?.length === 0 ||
                    (selectedContact?.length > 1 && !groupName)
                  }
                  className="w-full"
                  onClick={selectedContact?.length > 1 ? handleCreateGroup : handleStartNewChat}
                >
                  {selectedContact?.length > 1 ? t('chat.internal.createGroup') : t('chat.internal.createChat')}
                </ButtonLoading>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NewChat;
